import getConfig from 'next/config';
import { ServiceIdForMember } from '@/constants';
import { HeroImages } from '@/types/common';

const {
  publicRuntimeConfig: { NEXT_ASSET_URL },
} = getConfig();

const createHeroImages = (baseName: string): HeroImages => ({
  heroImageMobileURL: `${NEXT_ASSET_URL}/images/above-the-fold/${baseName}-mobile.jpg`,
  heroImageTabletURL: `${NEXT_ASSET_URL}/images/above-the-fold/${baseName}-tablet.jpg`,
  heroImageDesktopURL: `${NEXT_ASSET_URL}/images/above-the-fold/${baseName}-desktop.jpg`,
});

const ABOVE_THE_FOLD_HERO_IMAGES: { [key: string]: HeroImages } = Object.freeze({
  [ServiceIdForMember.SENIOR_CARE]: createHeroImages('senior-care'),
  [ServiceIdForMember.SENIOR_CARE_JOB]: createHeroImages('senior-care'),
  [ServiceIdForMember.PET_CARE]: createHeroImages('pet-care-general'),
  [ServiceIdForMember.PET_CARE_JOB]: createHeroImages('pet-care-general'),
  [ServiceIdForMember.HOUSEKEEPING]: createHeroImages('housekeeping'),
  [ServiceIdForMember.HOUSEKEEPING_JOB]: createHeroImages('housekeeping'),
  [ServiceIdForMember.TUTORING]: createHeroImages('tutoring'),
  [ServiceIdForMember.TUTORING_JOB]: createHeroImages('tutoring'),
  [ServiceIdForMember.SPECIAL_NEEDS]: createHeroImages('special-needs'),
  [ServiceIdForMember.SPECIAL_NEEDS_JOB]: createHeroImages('special-needs'),
  [ServiceIdForMember.CARE_GIGS]: createHeroImages('personal-assistant'),
  [ServiceIdForMember.CARE_GIGS_JOB]: createHeroImages('personal-assistant'),
  [ServiceIdForMember.CHILD_CARE]: createHeroImages('child-care'),
  [ServiceIdForMember.CHILD_CARE_JOB]: createHeroImages('child-care'),
  newborn: createHeroImages('newborn'),
  petCareCat: createHeroImages('pet-care-cat'),
  petCareBird: createHeroImages('pet-care-bird'),
});

const getHeroImages = (seoSegmentId: string, serviceIdForMember: string): HeroImages => {
  // separate the segment from the rest of the url (e.g. /child-care/boston-ma -> /child-care)
  const segment = `/${seoSegmentId.split('/')[1]}`;

  const segmentationsWithCatImage = [
    '/cat-sitters',
    '/cat-groomers',
    '/cat-boarding',
    '/cat-trainers',
    '/cat-sitting-jobs',
  ];

  const segmentationsWithNewbornImage = [
    '/newborn-nannies',
    '/infant-nannies',
    '/infant-child-care',
    '/infant-babysitters',
    '/night-nannies',
    '/infant-daycare',
  ];

  if (
    serviceIdForMember === ServiceIdForMember.BABYSITTER ||
    serviceIdForMember === ServiceIdForMember.NANNY
  ) {
    return ABOVE_THE_FOLD_HERO_IMAGES[ServiceIdForMember.CHILD_CARE];
  }

  if (serviceIdForMember === ServiceIdForMember.TUTORING_AFTER_SCHOOL_CARE) {
    return ABOVE_THE_FOLD_HERO_IMAGES[ServiceIdForMember.TUTORING];
  }

  if (serviceIdForMember === ServiceIdForMember.PET_DAY_CARE_LOCATION) {
    return ABOVE_THE_FOLD_HERO_IMAGES[ServiceIdForMember.PET_CARE];
  }

  if (segmentationsWithCatImage.includes(segment)) {
    return ABOVE_THE_FOLD_HERO_IMAGES.petCareCat;
  }

  if (segment.includes('/bird-sitters') || segment.includes('/bird-sitting-jobs')) {
    return ABOVE_THE_FOLD_HERO_IMAGES.petCareBird;
  }

  if (segmentationsWithNewbornImage.includes(segment)) {
    return ABOVE_THE_FOLD_HERO_IMAGES.newborn;
  }

  // default images to child care if no match
  return (
    ABOVE_THE_FOLD_HERO_IMAGES[serviceIdForMember] ||
    ABOVE_THE_FOLD_HERO_IMAGES[ServiceIdForMember.CHILD_CARE]
  );
};

export { getHeroImages, ABOVE_THE_FOLD_HERO_IMAGES };
